import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Paper,
  alpha,
  useTheme,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as PlusIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  NotificationsActive as RequestIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const GOLD = "#D4AF37";

export default function ManageInvites() {
  const { id } = useParams();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [members, setMembers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);

  useEffect(() => {
    fetchMembers();
    fetchJoinRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem("jwttoken");
      const response = await axios.get(
        "https://tdtlworld.com/dss-kaas-backend/socialapp/users/",
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMembers(
        response.data.map((user) => ({
          id: user.user_id,
          name: `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim(),
          email: user.email,
          profilePic: user.profile_pic || "",
        }))
      );
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const fetchJoinRequests = async () => {
    try {
      const token = localStorage.getItem("jwttoken");
      const response = await axios.get(
        `https://tdtlworld.com/dss-kaas-backend/socialapp/group/${id}/join-requests/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJoinRequests(response.data);
    } catch (error) {
      console.error("Error fetching join requests:", error);
    }
  };

  const handleApproveReject = async (requestId, action) => {
    try {
      const token = localStorage.getItem("jwttoken");
      await axios.patch(
        `https://tdtlworld.com/dss-kaas-backend/socialapp/join-requests/${requestId}/`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      Swal.fire("Success", `Request ${action}ed successfully`, "success");
      fetchJoinRequests();
    } catch (error) {
      Swal.fire("Error", `Failed to ${action} request`, "error");
    }
  };

  const handleAddMemberToGroup = async (member) => {
    try {
      const token = localStorage.getItem("jwttoken");
      const response = await axios.post(
        `https://tdtlworld.com/dss-kaas-backend/socialapp/groups/${id}/add-member/`,
        { users: [member.id] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 201) {
        Swal.fire("Success", `${member.name} has been added!`, "success");
      }
    } catch (error) {
      Swal.fire("Error", "Failed to add member.", "error");
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const filteredMembers = members.filter((m) =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ py: 2 }}>
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 4, 
          overflow: "hidden", 
          border: `1px solid ${alpha(GOLD, 0.1)}`,
          bgcolor: 'background.paper'
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': { fontWeight: 700, p: 3 },
              '& .MuiTabs-indicator': { bgcolor: GOLD }
            }}
          >
            <Tab label="Send Invites" icon={<PersonAddIcon />} iconPosition="start" />
            <Tab label="Join Requests" icon={<RequestIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <Box sx={{ p: 4 }}>
          <AnimatePresence mode="wait">
            {activeTab === 0 ? (
              <motion.div
                key="invites"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontFamily: "'Outfit', sans-serif" }}>
                      Search Members to Invite
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Search member name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ mb: 3 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: GOLD }} />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: 3 }
                      }}
                    />

                    <List sx={{ 
                      maxHeight: 400, 
                      overflowY: "auto", 
                      bgcolor: alpha(GOLD, 0.02),
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      {filteredMembers.map((member) => (
                        <ListItem key={member.id}>
                          <ListItemAvatar>
                            <Avatar src={member.profilePic}>{member.name.charAt(0)}</Avatar>
                          </ListItemAvatar>
                          <ListItemText primary={member.name} secondary={member.email} />
                          <ListItemSecondaryAction>
                            <IconButton 
                              onClick={() => handleAddMemberToGroup(member)}
                              sx={{ color: GOLD, bgcolor: alpha(GOLD, 0.1), '&:hover': { bgcolor: GOLD, color: 'white' } }}
                            >
                              <PlusIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                      {filteredMembers.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">No members found</Typography>
                        </Box>
                      )}
                    </List>
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ borderLeft: { md: `1px solid ${alpha(theme.palette.divider, 0.5)}` }, pl: { md: 6 } }}>
                    <Box sx={{ textAlign: 'center', py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                       <Avatar sx={{ width: 80, height: 80, bgcolor: alpha(GOLD, 0.1), color: GOLD }}>
                         <PersonAddIcon sx={{ fontSize: 40 }} />
                       </Avatar>
                       <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>Expansion</Typography>
                       <Typography variant="body2" color="text.secondary" align="center">
                         Invite users to join your community group. Growing your circle leads to better collaboration and shared knowledge.
                       </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </motion.div>
            ) : (
              <motion.div
                key="requests"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, fontFamily: "'Outfit', sans-serif" }}>
                  Pending Join Requests
                </Typography>
                
                {joinRequests.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography color="text.secondary">No pending join requests found at this time.</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {joinRequests.map((request) => (
                      <Grid item xs={12} key={request.id}>
                        <Card variant="outlined" sx={{ borderRadius: 3, transition: '0.2s', '&:hover': { borderColor: GOLD } }}>
                          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '12px !important' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: GOLD }}>{request.requested_by.name.charAt(0)}</Avatar>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{request.requested_by.name}</Typography>
                                <Typography variant="caption" color="text.secondary">Wants to join the group</Typography>
                              </Box>
                            </Box>

                            <Box>
                              {request.status === 'pending' ? (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <IconButton 
                                    onClick={() => handleApproveReject(request.id, 'approve')}
                                    sx={{ bgcolor: '#4caf50', color: 'white', '&:hover': { bgcolor: '#388e3c' } }}
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    onClick={() => handleApproveReject(request.id, 'reject')}
                                    sx={{ bgcolor: '#f44336', color: 'white', '&:hover': { bgcolor: '#d32f2f' } }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Chip 
                                  label={request.status.toUpperCase()} 
                                  color={request.status === 'approved' ? 'success' : 'error'} 
                                  size="small"
                                  sx={{ fontWeight: 700 }}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Paper>
    </Box>
  );
}
